/**
 * Bad Mood Crusher
 * A warm and healing emotional transformation app
 */

import './styles.css';
import shredderIcon from './assets/images/shredder.png';
import washerIcon from './assets/images/washer.png';
import toasterIcon from './assets/images/toaster.png';
import juicerIcon from './assets/images/juicer.png';
import balloonIcon from './assets/images/balloon.png';
import jarIcon from './assets/images/jar.png';
import bubbleIcon from './assets/images/bubble.png';
import recycleIcon from './assets/images/recycle.png';
import freezeIcon from './assets/images/freeze.png';
import plantIcon from './assets/images/plant.png';
import burnIcon from './assets/images/burn.png';
import lockIcon from './assets/images/lock.png';

// 应用状态
type AppState = 'idle' | 'processing' | 'done';

interface Tool {
  id: string;
  name: string;
  icon: string;
  description: string;
  endMessage: string;
}

// Example emotion chips - expanded library
const emotionLibrary = [
  'So annoyed', 'A bit anxious', 'Misunderstood',
  'So stressed', 'Feeling stuck', 'So tired',
  'A bit sad', 'Don\'t wanna talk', 'Feeling down',
  'Really angry', 'Hurt', 'So disappointed',
  'Can\'t sleep', 'Wanna cry', 'Restless',
  'Lonely', 'Powerless', 'Lost',
  'Had enough', 'Frustrated', 'Mind racing'
];

let currentEmotions = ['So annoyed', 'A bit anxious', 'Misunderstood'];

// Randomly select 3 unique emotions
function getRandomEmotions(): string[] {
  const shuffled = [...emotionLibrary].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 3);
}

// 16 emotional processing tools (4x4 layout)
const tools: Tool[] = [
  {
    id: 'shredder',
    name: 'Shred',
    icon: shredderIcon,
    description: 'Paper Shredder',
    endMessage: 'Shredded, now lighter.'
  },
  {
    id: 'bubble',
    name: 'Bubble',
    icon: bubbleIcon,
    description: 'Bubble Machine',
    endMessage: 'Let it gently float away.'
  },
  {
    id: 'toaster',
    name: 'Toast',
    icon: toasterIcon,
    description: 'Toaster',
    endMessage: 'Bad moods can smell good too.'
  },
  {
    id: 'washer',
    name: 'Wash',
    icon: washerIcon,
    description: 'Washing Machine',
    endMessage: 'Washed clean, mind refreshed.'
  },
  {
    id: 'juicer',
    name: 'Juice',
    icon: juicerIcon,
    description: 'Juicer',
    endMessage: 'Turn the bitter into sweet.'
  },
  {
    id: 'recycle',
    name: 'Recycle',
    icon: recycleIcon,
    description: 'Recycle Bin',
    endMessage: 'Recycled into new energy.'
  },
  {
    id: 'balloon',
    name: 'Send',
    icon: balloonIcon,
    description: 'Balloon Post',
    endMessage: 'Send it away, make some space.'
  },
  {
    id: 'jar',
    name: 'Store',
    icon: jarIcon,
    description: 'Storage Jar',
    endMessage: 'Stored for now, no rush.'
  },
  {
    id: 'freeze',
    name: 'Freeze',
    icon: freezeIcon,
    description: 'Freezer',
    endMessage: 'Freeze it, cool down a bit.'
  },
  {
    id: 'plant',
    name: 'Plant',
    icon: plantIcon,
    description: 'Plant Pot',
    endMessage: 'Plant it into new possibilities.'
  },
  {
    id: 'burn',
    name: 'Burn',
    icon: burnIcon,
    description: 'Ignite & Disperse',
    endMessage: 'Burn it to light, scatter in the wind.'
  },
  {
    id: 'lock',
    name: 'Lock',
    icon: lockIcon,
    description: 'Safe Box',
    endMessage: 'Lock it away till you\'re ready.'
  }
];

// Subtitle carousel messages
const subtitles = [
  'Writing it down is already great.',
  'Just leave it here for today.',
  'Give yourself a little buffer.',
  'No need to feel better right away.',
  'Allow yourself to have emotions.'
];

let currentState: AppState = 'idle';
let currentText = '';
let currentSubtitleIndex = 0;

// 创建角色元素
function createCharacter(): HTMLElement {
  const character = document.createElement('div');
  character.className = 'character';
  character.innerHTML = `
    <div class="character-body">
      <div class="character-face">
        <div class="eye left"></div>
        <div class="eye right"></div>
        <div class="mouth"></div>
      </div>
    </div>
  `;
  return character;
}

// 创建便签输入区
function createNotepad(): HTMLElement {
  const notepadContainer = document.createElement('div');
  notepadContainer.className = 'notepad-container';

  const notepad = document.createElement('div');
  notepad.className = 'notepad';

  const tape = document.createElement('div');
  tape.className = 'tape';

  // 清空按钮
  const clearBtn = document.createElement('button');
  clearBtn.className = 'clear-btn';
  clearBtn.innerHTML = '🗑️';
  clearBtn.title = 'Clear';
  clearBtn.style.display = 'none';

  const textarea = document.createElement('textarea');
  textarea.className = 'notepad-input';
  textarea.placeholder = 'Write your bad mood on this note...';
  textarea.maxLength = 120;

  const emoji = document.createElement('div');
  emoji.className = 'notepad-emoji';
  emoji.textContent = '🙂';

  // Example chips container (declare early so event listeners can reference it)
  const examplesContainer = document.createElement('div');
  examplesContainer.className = 'examples-container';

  const examples = document.createElement('div');
  examples.className = 'example-chips';

  textarea.addEventListener('input', (e) => {
    const target = e.target as HTMLTextAreaElement;
    currentText = target.value;

    // Show/hide clear button
    clearBtn.style.display = currentText.length > 0 ? 'block' : 'none';

    // Auto-hide chips when user has typed something
    if (currentText.trim().length > 0) {
      examplesContainer.style.display = 'none';
    } else {
      examplesContainer.style.display = 'flex';
    }

    // Update emoji - refined emotional changes
    if (currentText.length === 0) {
      emoji.textContent = '🙂';
    } else if (currentText.length < 20) {
      emoji.textContent = '😐';
    } else if (currentText.length < 60) {
      emoji.textContent = '😔';
    } else {
      emoji.textContent = '🥺';
    }

    // Limit notification
    if (currentText.length >= 120) {
      showToast('That\'s enough for now');
    }

    // Every 5 characters, character nods and looks at notepad
    if (currentText.length % 5 === 0 && currentText.length > 0) {
      const characterBody = document.querySelector('.character-body') as HTMLElement;
      const character = document.querySelector('.character') as HTMLElement;
      if (characterBody && character) {
        characterBody.style.animation = 'none';
        character.classList.add('looking-at-notepad');
        setTimeout(() => {
          characterBody.style.animation = 'nod 0.3s ease';
        }, 10);
      }
    }

    // Update toolbar state
    updateToolbarState();
  });

  // Character looks at notepad when focused
  textarea.addEventListener('focus', () => {
    const character = document.querySelector('.character') as HTMLElement;
    if (character) {
      character.classList.add('looking-at-notepad');
    }
  });

  textarea.addEventListener('blur', () => {
    const character = document.querySelector('.character') as HTMLElement;
    if (character && currentText.length === 0) {
      character.classList.remove('looking-at-notepad');
    }
  });

  notepad.appendChild(tape);
  notepad.appendChild(clearBtn);
  notepad.appendChild(textarea);
  notepad.appendChild(emoji);

  notepadContainer.appendChild(notepad);

  // Render chips
  const renderChips = () => {
    examples.innerHTML = '';
    currentEmotions.forEach(emotion => {
      const chip = document.createElement('button');
      chip.className = 'example-chip';
      chip.textContent = emotion;
      chip.addEventListener('click', () => {
        textarea.value = emotion;
        currentText = emotion;
        textarea.dispatchEvent(new Event('input'));
        textarea.focus();
      });
      examples.appendChild(chip);
    });
  };

  renderChips();

  // Refresh button (inline with chips)
  const refreshBtn = document.createElement('button');
  refreshBtn.className = 'refresh-chips-btn';
  refreshBtn.innerHTML = '↻';
  refreshBtn.title = 'Shuffle suggestions';
  refreshBtn.addEventListener('click', () => {
    currentEmotions = getRandomEmotions();
    renderChips();
    // Add subtle animation
    examples.style.animation = 'none';
    setTimeout(() => {
      examples.style.animation = 'fadeIn 0.3s ease';
    }, 10);
  });

  examplesContainer.appendChild(examples);
  examplesContainer.appendChild(refreshBtn);

  // 清空按钮点击事件(需要在 examplesContainer 之后绑定)
  clearBtn.addEventListener('click', () => {
    if (confirm('Clear this note?')) {
      textarea.value = '';
      currentText = '';
      emoji.textContent = '🙂';
      clearBtn.style.display = 'none';
      examplesContainer.style.display = 'flex'; // 重新显示 chips
      updateToolbarState();
    }
  });

  notepadContainer.appendChild(examplesContainer);

  return notepadContainer;
}

// Update toolbar button state
function updateToolbarState() {
  const buttons = document.querySelectorAll('.tool-button') as NodeListOf<HTMLButtonElement>;
  buttons.forEach(button => {
    if (currentText.trim().length === 0) {
      button.classList.add('disabled');
    } else {
      button.classList.remove('disabled');
    }
  });
}

// Create toolbar
function createToolbar(): HTMLElement {
  const toolbarContainer = document.createElement('div');
  toolbarContainer.className = 'toolbar-container';

  // Toolbar title
  const toolbarTitle = document.createElement('div');
  toolbarTitle.className = 'toolbar-title';
  toolbarTitle.textContent = 'Choose a way to process';

  // 机器舞台底座
  const stage = document.createElement('div');
  stage.className = 'machine-stage';
  stage.innerHTML = `
    <div class="stage-platform">
      <div class="stage-rail"></div>
      <div class="stage-rail"></div>
    </div>
  `;

  const toolbar = document.createElement('div');
  toolbar.className = 'toolbar';

  tools.forEach(tool => {
    const button = document.createElement('button');
    button.className = 'tool-button disabled';
    button.setAttribute('data-tool-id', tool.id);

    // 判断是图片URL还是emoji
    const iconHTML = (tool.icon.startsWith('data:') || tool.icon.startsWith('/') || tool.icon.includes('.webp'))
      ? `<img src="${tool.icon}" alt="${tool.name}" class="tool-icon-img">`
      : `<div class="tool-icon">${tool.icon}</div>`;

    button.innerHTML = `
      ${iconHTML}
      <div class="tool-name">${tool.name}</div>
    `;

    button.addEventListener('click', () => handleToolClick(tool));
    toolbar.appendChild(button);
  });

  toolbarContainer.appendChild(stage);
  toolbarContainer.appendChild(toolbarTitle);
  toolbarContainer.appendChild(toolbar);

  return toolbarContainer;
}

// Show toast message
function showToast(message: string) {
  const toast = document.createElement('div');
  toast.className = 'toast';
  toast.textContent = message;
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.classList.add('show');
  }, 10);

  setTimeout(() => {
    toast.classList.remove('show');
    setTimeout(() => toast.remove(), 300);
  }, 2000);
}

// Handle tool click
function handleToolClick(tool: Tool) {
  if (currentState !== 'idle') return;

  if (!currentText.trim()) {
    // Notepad shake
    const notepad = document.querySelector('.notepad') as HTMLElement;
    if (notepad) {
      notepad.style.animation = 'shake 0.5s ease';
      setTimeout(() => {
        notepad.style.animation = '';
      }, 500);
    }
    showToast('Write something first');
    return;
  }

  // 点击时给工具卡片添加视觉反馈
  const clickedButton = document.querySelector(`[data-tool-id="${tool.id}"]`) as HTMLElement;
  if (clickedButton) {
    clickedButton.style.transform = 'scale(0.95)';
    clickedButton.style.transition = 'transform 0.1s ease';

    setTimeout(() => {
      clickedButton.style.transform = '';
    }, 150);
  }

  playAnimation(tool);
}

// Play animation
function playAnimation(tool: Tool) {
  currentState = 'processing';

  // Character puts on goggles/ready state
  const character = document.querySelector('.character') as HTMLElement;
  if (character) {
    character.classList.add('processing');
  }

  // 创建动画舞台
  const stage = document.createElement('div');
  stage.className = 'animation-stage';
  stage.id = `animation-${tool.id}`;

  const notepad = document.querySelector('.notepad') as HTMLElement;
  const textarea = document.querySelector('.notepad-input') as HTMLTextAreaElement;

  // 添加轻柔遮罩 (优化: 从黑色改为温柔的奶白色)
  const overlay = document.createElement('div');
  overlay.className = 'overlay';
  document.body.appendChild(overlay);

  // 便签克隆 - 添加飞入动画
  const notepadRect = notepad.getBoundingClientRect();
  const stageCenter = {
    x: window.innerWidth / 2,
    y: window.innerHeight / 2
  };

  // 根据不同工具设置动画时长 (优化: 统一缩短到4-4.5秒)
  const durations: { [key: string]: number } = {
    'shredder': 3800,
    'bubble': 4200,
    'toaster': 4900,
    'washer': 4000,
    'juicer': 4500,
    'monster': 4000,
    'balloon': 4500,
    'jar': 3800,
    'freeze': 4200,
    'plant': 4800,
    'burn': 4200,
    'origami': 4800,
    'melt': 4000,
    'recycle': 4500,
    'lock': 4000,
    'star': 4200
  };

  const duration = durations[tool.id] || 4200;

  // 根据不同工具播放不同动画
  switch (tool.id) {
    case 'shredder':
      playShredderAnimation(stage, notepad);
      break;
    case 'bubble':
      playBubbleAnimation(stage, currentText);
      break;
    case 'toaster':
      playToasterAnimation(stage, notepad);
      break;
    case 'washer':
      playWasherAnimation(stage, notepad);
      break;
    case 'juicer':
      playJuicerAnimation(stage, notepad);
      break;
    case 'monster':
      playMonsterAnimation(stage, notepad);
      break;
    case 'balloon':
      playBalloonAnimation(stage, notepad);
      break;
    case 'jar':
      playJarAnimation(stage, notepad);
      break;
    case 'freeze':
      playFreezeAnimation(stage, notepad);
      break;
    case 'plant':
      playPlantAnimation(stage, notepad);
      break;
    case 'burn':
      playBurnAnimation(stage, notepad);
      break;
    case 'origami':
      playOrigamiAnimation(stage, notepad);
      break;
    case 'melt':
      playGenericAnimation(stage, notepad, tool);
      break;
    case 'recycle':
      playRecycleAnimation(stage, notepad);
      break;
    case 'lock':
      playLockboxAnimation(stage, notepad);
      break;
    case 'star':
      // 新工具使用通用动画
      playGenericAnimation(stage, notepad, tool);
      break;
  }

  document.body.appendChild(stage);

  // 动画结束
  setTimeout(() => {
    currentState = 'done';

    // 先显示结束文案 (优化: 在动画结束前0.3秒提前显示)
    showEndMessage(tool.endMessage);

    // 遮罩和舞台渐出
    overlay.style.animation = 'fadeOut 0.4s ease-out forwards';
    stage.style.animation = 'fadeOut 0.4s ease-out forwards';

    setTimeout(() => {
      overlay.remove();
      stage.remove();
    }, 400);

    // 角色恢复正常
    if (character) {
      character.classList.remove('processing');
      character.classList.remove('looking-at-notepad');
    }

    // 清空输入并重置状态 (优化: 延迟1.2秒,给用户看完文案的时间)
    setTimeout(() => {
      textarea.value = '';
      currentText = '';
      const emoji = document.querySelector('.notepad-emoji') as HTMLElement;
      const clearBtn = document.querySelector('.clear-btn') as HTMLElement;
      const examplesContainer = document.querySelector('.examples-container') as HTMLElement;
      if (emoji) emoji.textContent = '🙂';
      if (clearBtn) clearBtn.style.display = 'none';
      if (examplesContainer) examplesContainer.style.display = 'flex'; // 重新显示 chips
      updateToolbarState();
      currentState = 'idle';
    }, 1200);
  }, duration);
}

// 碎纸机动画 - 优化版 3.8s
function playShredderAnimation(stage: HTMLElement, notepad: HTMLElement) {
  const shredder = document.createElement('div');
  shredder.className = 'shredder';
  shredder.innerHTML = `
    <div class="shredder-body">
      <div class="shredder-slot"></div>
    </div>
  `;

  const noteClone = notepad.cloneNode(true) as HTMLElement;
  noteClone.className = 'notepad-clone';

  stage.appendChild(shredder);
  stage.appendChild(noteClone);

  // 阶段1: 机器入场 0.4s
  setTimeout(() => {
    shredder.style.animation = 'shredderEntry 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';
  }, 0);

  // 阶段2: 便签飞向投递口 0.6s (关键优化!)
  setTimeout(() => {
    noteClone.style.animation = 'notepadFlyToShredder 0.6s ease-in forwards';
  }, 400);

  // 阶段3: 被吸入槽口 0.5s
  setTimeout(() => {
    noteClone.style.animation = 'notepadPullIn 0.5s cubic-bezier(0.42, 0, 0.58, 1) forwards';
  }, 1000);

  // 阶段4: 咔嚓碎裂 1.2s (爽点)
  setTimeout(() => {
    noteClone.remove();

    // 生成彩色纸屑 - 4种颜色,3种尺寸
    const colors = ['#FFB3BA', '#FFFFBA', '#BAE1FF', '#BAFFC9'];
    const sizes = [
      { w: 6, h: 16 },
      { w: 8, h: 20 },
      { w: 10, h: 24 }
    ];

    for (let i = 0; i < 40; i++) {
      setTimeout(() => {
        const confetti = document.createElement('div');
        confetti.className = 'confetti';
        const size = sizes[Math.floor(Math.random() * sizes.length)];
        confetti.style.width = `${size.w}px`;
        confetti.style.height = `${size.h}px`;
        confetti.style.left = `${45 + Math.random() * 10}%`;
        confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        confetti.style.animationDelay = `${Math.random() * 0.3}s`;
        stage.appendChild(confetti);
      }, i * 30);
    };
  }, 1500);

  // 阶段5: 收尾 1.1s - 纸屑渐散
}

// 泡泡动画 - 专业规格 4.8s
function playBubbleAnimation(stage: HTMLElement, text: string) {
  const bubbleGun = document.createElement('div');
  bubbleGun.className = 'bubble-gun';
  bubbleGun.textContent = '🫧';
  stage.appendChild(bubbleGun);

  // 入场 0.5s (已在CSS中处理)

  // 转化 1.2s - 文字逐个变成泡泡
  const chars = text.split('').filter(c => c.trim()); // 过滤空格
  chars.forEach((char, i) => {
    setTimeout(() => {
      const bubble = document.createElement('div');
      bubble.className = 'bubble';
      bubble.textContent = char;

      // 泡泡大小变化 - 小中大随机
      const sizes = [35, 42, 50];
      const size = sizes[Math.floor(Math.random() * sizes.length)];
      bubble.style.width = `${size}px`;
      bubble.style.height = `${size}px`;
      bubble.style.fontSize = `${size * 0.35}px`;
      bubble.style.display = 'flex';
      bubble.style.alignItems = 'center';
      bubble.style.justifyContent = 'center';

      // 水平位置分散
      bubble.style.left = `${25 + Math.random() * 50}%`;

      // 动画延迟错开
      bubble.style.animationDelay = `${i * 0.05}s`;

      stage.appendChild(bubble);
    }, 500 + i * 80); // 0.5s入场后,每80ms生成一个
  });

  // 上升 2.6s - 泡泡缓慢上升
  // 破裂 0.5s - 顶部渐散
  // (通过CSS bubbleFloat动画处理)
}

// 烤面包机动画 - 专业规格 5.2s
function playToasterAnimation(stage: HTMLElement, notepad: HTMLElement) {
  const toaster = document.createElement('div');
  toaster.className = 'toaster';
  toaster.innerHTML = `
    <div class="toaster-body">
      <div class="toaster-slot"></div>
    </div>
  `;

  const noteClone = notepad.cloneNode(true) as HTMLElement;
  noteClone.className = 'notepad-clone';
  noteClone.style.top = '50%';
  noteClone.style.left = '50%';

  stage.appendChild(toaster);
  stage.appendChild(noteClone);

  // 阶段1: 烤面包机入场 0.4s

  // 阶段2: 便签折叠缩小 0.6s (关键优化!)
  setTimeout(() => {
    noteClone.style.animation = 'notepadFoldToToast 0.6s cubic-bezier(0.68, -0.55, 0.27, 1.55) forwards';
  }, 400);

  // 阶段3: 变成面包片 0.3s
  setTimeout(() => {
    // 清空便签内容,改成面包片emoji
    noteClone.innerHTML = '🍞';
    noteClone.style.fontSize = '48px';
    noteClone.style.background = 'transparent';
    noteClone.style.border = 'none';
    noteClone.style.boxShadow = 'none';
    noteClone.style.padding = '0';
    noteClone.style.opacity = '0';
    noteClone.style.transform = 'translate(-50%, -50%) scale(0.2)';
    noteClone.style.animation = 'breadAppear 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards';
  }, 1000);

  // 阶段4: 面包片飞向槽口并掉入 0.9s
  setTimeout(() => {
    noteClone.style.animation = 'breadFlyToSlot 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards';

    // 掉入槽口
    setTimeout(() => {
      noteClone.style.animation = 'breadDropIntoSlot 0.4s cubic-bezier(0.6, 0.04, 0.98, 0.34) forwards';

      setTimeout(() => {
        noteClone.remove();
      }, 400);
    }, 500);
  }, 1300);

  // 阶段5: 烘烤 2.0s - 等待烘烤(可选:添加发热效果)
  setTimeout(() => {
    // 可选:添加烘烤时的光效
    const heatGlow = document.createElement('div');
    heatGlow.style.cssText = `
      position: absolute;
      top: 35%;
      left: 50%;
      transform: translateX(-50%);
      width: 160px;
      height: 40px;
      background: radial-gradient(ellipse, rgba(255, 215, 0, 0.4), transparent);
      filter: blur(8px);
      animation: pulse 0.8s ease-in-out infinite;
    `;
    stage.appendChild(heatGlow);

    setTimeout(() => heatGlow.remove(), 2000);
  }, 2200);

  // 阶段6: 叮!弹出 0.8s
  setTimeout(() => {
    const toast = document.createElement('div');
    toast.className = 'toast-pop';
    toast.textContent = '🍞✨';
    stage.appendChild(toast);
  }, 4200);

  // 余韵 0.4s - 面包自然停留
}

// 洗衣机动画 - 优化版 4.0s
function playWasherAnimation(stage: HTMLElement, notepad: HTMLElement) {
  const washer = document.createElement('div');
  washer.className = 'washer';
  washer.innerHTML = `
    <div class="washer-body">
      <div class="washer-window">
        <div class="washer-spin"></div>
      </div>
    </div>
  `;

  const noteClone = notepad.cloneNode(true) as HTMLElement;
  noteClone.className = 'notepad-clone';

  stage.appendChild(washer);
  stage.appendChild(noteClone);

  // 阶段1: 机器入场 0.4s
  setTimeout(() => {
    washer.style.animation = 'washerEntry 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';
  }, 0);

  // 阶段2: 便签揉成球 0.5s (关键优化!)
  setTimeout(() => {
    noteClone.style.animation = 'notepadCrumple 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55) forwards';
  }, 400);

  // 阶段3: 纸球飞向洗衣机窗口 0.4s
  setTimeout(() => {
    noteClone.style.animation = 'notepadFlyToWasher 0.4s ease-in forwards';
  }, 900);

  // 阶段4: 掉入洗衣机 0.3s
  setTimeout(() => {
    noteClone.style.animation = 'notepadBallDrop 0.3s cubic-bezier(0.6, 0.04, 0.98, 0.34) forwards';
  }, 1300);

  // 阶段5: 洗涤 1.8s - 旋转+泡沫
  setTimeout(() => {
    noteClone.style.animation = 'notepadWashing 1.8s linear forwards';

    // 生成泡沫粒子
    const washerWindow = washer.querySelector('.washer-window') as HTMLElement;
    if (washerWindow) {
      for (let i = 0; i < 15; i++) {
        setTimeout(() => {
          const foam = document.createElement('div');
          foam.className = 'foam-bubble';
          const size = 8 + Math.random() * 12;
          foam.style.width = `${size}px`;
          foam.style.height = `${size}px`;
          foam.style.left = `${20 + Math.random() * 60}%`;
          foam.style.top = `${20 + Math.random() * 60}%`;
          foam.style.animationDelay = `${Math.random() * 0.5}s`;
          washerWindow.appendChild(foam);

          setTimeout(() => foam.remove(), 1800);
        }, i * 100);
      }
    }
  }, 1600);

  // 阶段6: 出水 0.6s - 洁净效果
  setTimeout(() => {
    const sparkle = document.createElement('div');
    sparkle.style.cssText = `
      position: absolute;
      top: 30%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 3rem;
      animation: sparkleEffect 0.6s ease-out forwards;
    `;
    sparkle.textContent = '✨';
    stage.appendChild(sparkle);

    setTimeout(() => sparkle.remove(), 600);
  }, 3400);
}

// 榨汁机动画 - 优化版 4.5s
function playJuicerAnimation(stage: HTMLElement, notepad: HTMLElement) {
  const juicer = document.createElement('div');
  juicer.className = 'juicer';
  juicer.innerHTML = `
    <div class="juicer-body">
      <div class="juice-label">💚</div>
      <div class="juicer-blade"></div>
    </div>
  `;

  const noteClone = notepad.cloneNode(true) as HTMLElement;
  noteClone.className = 'notepad-clone';

  stage.appendChild(juicer);
  stage.appendChild(noteClone);

  // 阶段1: 入场 0.4s
  setTimeout(() => {
    juicer.style.animation = 'juicerEntry 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';
  }, 0);

  // 阶段2: 便签撕裂成碎片 0.6s (关键优化!)
  setTimeout(() => {
    noteClone.style.animation = 'notepadTear 0.6s ease-out forwards';
  }, 400);

  // 阶段3: 变成水果块掉入 0.8s
  setTimeout(() => {
    noteClone.remove();

    // 生成5个水果块掉入
    const colors = ['#FBBF24', '#F59E0B', '#FB923C', '#FDBA74', '#FED7AA'];
    for (let i = 0; i < 5; i++) {
      setTimeout(() => {
        const fruit = document.createElement('div');
        fruit.className = 'fruit-piece';
        fruit.style.cssText = `
          position: absolute;
          width: ${20 + Math.random() * 15}px;
          height: ${20 + Math.random() * 15}px;
          background: ${colors[i]};
          border-radius: 6px;
          top: 30%;
          left: ${45 + Math.random() * 10}%;
          box-shadow: 0 4px 12px rgba(251, 191, 36, 0.4);
        `;
        fruit.style.animation = `fruitDrop ${0.6 + Math.random() * 0.2}s cubic-bezier(0.6, 0.04, 0.98, 0.34) forwards`;
        fruit.style.animationDelay = `${i * 0.08}s`;
        stage.appendChild(fruit);

        setTimeout(() => fruit.remove(), 1000);
      }, i * 80);
    }
  }, 1000);

  // 阶段4: 榨汁 1.6s - 旋转搅拌
  setTimeout(() => {
    const blade = juicer.querySelector('.juicer-blade') as HTMLElement;
    if (blade) {
      blade.style.opacity = '1';
      blade.classList.add('spinning');

      // 颜色从浅到饱和
      const juicerBody = juicer.querySelector('.juicer-body') as HTMLElement;
      if (juicerBody) {
        juicerBody.style.transition = 'background 1.6s ease-out';
        juicerBody.style.background = 'linear-gradient(135deg, #A7F3D0 0%, #6EE7B7 100%)';
      }

      // 调整速度节奏
      setTimeout(() => blade.style.animationDuration = '0.5s', 400);
      setTimeout(() => blade.style.animationDuration = '0.25s', 800);

      // 生成泡泡
      for (let i = 0; i < 10; i++) {
        setTimeout(() => {
          const bubble = document.createElement('div');
          bubble.style.cssText = `
            position: absolute;
            width: ${6 + Math.random() * 8}px;
            height: ${6 + Math.random() * 8}px;
            background: rgba(255, 255, 255, 0.6);
            border-radius: 50%;
            top: 45%;
            left: ${40 + Math.random() * 20}%;
            animation: bubbleFloat 1s ease-out forwards;
          `;
          juicer.querySelector('.juicer-body')?.appendChild(bubble);
          setTimeout(() => bubble.remove(), 1000);
        }, i * 120);
      }
    }
  }, 1800);

  // 阶段5: 倒出 1.1s - 果汁流出
  setTimeout(() => {
    const blade = juicer.querySelector('.juicer-blade') as HTMLElement;
    if (blade) blade.style.opacity = '0';

    const juicerBody = juicer.querySelector('.juicer-body') as HTMLElement;
    if (juicerBody) {
      juicerBody.style.transition = 'transform 0.4s ease-out';
      juicerBody.style.transform = 'rotate(25deg)';
    }

    // 显示杯子
    const cup = document.createElement('div');
    cup.className = 'juice-cup';
    cup.innerHTML = '<div class="juice-fill"></div>';
    juicer.appendChild(cup);

    // 果汁流
    setTimeout(() => {
      const flow = document.createElement('div');
      flow.className = 'juice-flow';
      juicer.appendChild(flow);
      setTimeout(() => flow.remove(), 1000);
    }, 200);

    // 星光效果
    setTimeout(() => {
      for (let i = 0; i < 3; i++) {
        const star = document.createElement('div');
        star.textContent = '✨';
        star.style.cssText = `
          position: absolute;
          font-size: 1.5rem;
          top: ${60 + i * 8}%;
          left: ${45 + Math.random() * 10}%;
          animation: sparkleEffect 0.8s ease-out forwards;
          animation-delay: ${i * 0.15}s;
        `;
        stage.appendChild(star);
        setTimeout(() => star.remove(), 1200);
      }
    }, 600);
  }, 3400);
}

// 小怪兽动画 - 专业规格 4.6s (软糯可爱风格)
function playMonsterAnimation(stage: HTMLElement, notepad: HTMLElement) {
  const monster = document.createElement('div');
  monster.className = 'monster';
  monster.innerHTML = `
    <div class="monster-body">
      <div class="monster-eyes">
        <div class="monster-eye"></div>
        <div class="monster-eye"></div>
      </div>
      <div class="monster-mouth"></div>
      <div class="monster-cheek left"></div>
      <div class="monster-cheek right"></div>
    </div>
  `;

  const noteClone = notepad.cloneNode(true) as HTMLElement;
  noteClone.className = 'notepad-clone';
  noteClone.style.top = '50%';
  noteClone.style.left = '50%';

  stage.appendChild(monster);
  stage.appendChild(noteClone);

  // 入场 0.4s (已在CSS中处理)

  // 闻一闻 0.8s
  setTimeout(() => {
    const monsterBody = monster.querySelector('.monster-body') as HTMLElement;
    if (monsterBody) {
      monsterBody.classList.add('sniffing');
    }
  }, 400);

  // 靠近 0.8s - 便签被吸引
  setTimeout(() => {
    noteClone.style.animation = 'notepadAttracted 0.8s ease-in-out forwards';
  }, 1200);

  // 吞下 1.2s - 一口吞+咀嚼
  setTimeout(() => {
    const mouth = monster.querySelector('.monster-mouth') as HTMLElement;

    // 嘴巴张大
    if (mouth) {
      mouth.classList.add('open');
    }

    // 便签被吸进嘴里
    setTimeout(() => {
      noteClone.style.animation = 'notepadEaten 0.6s cubic-bezier(0.6, 0.04, 0.98, 0.34) forwards';
    }, 250);

    // 嘴巴合上+开始咀嚼
    setTimeout(() => {
      if (mouth) {
        mouth.classList.remove('open');
      }

      const leftCheek = monster.querySelector('.monster-cheek.left') as HTMLElement;
      const rightCheek = monster.querySelector('.monster-cheek.right') as HTMLElement;

      // 第一次咀嚼 - 腮帮鼓起
      if (leftCheek && rightCheek) {
        leftCheek.style.animation = 'chewCheek 0.5s ease-in-out';
        rightCheek.style.animation = 'chewCheek 0.5s ease-in-out';

        // 第二次咀嚼
        setTimeout(() => {
          leftCheek.style.animation = 'chewCheek 0.5s ease-in-out';
          rightCheek.style.animation = 'chewCheek 0.5s ease-in-out';
        }, 500);
      }
    }, 750);
  }, 2000);

  // 咕噜嗝 1.2s - 吐出爱心
  setTimeout(() => {
    const monsterBody = monster.querySelector('.monster-body') as HTMLElement;
    if (monsterBody) {
      monsterBody.classList.remove('sniffing');
      monsterBody.classList.add('burping');
    }

    // 吐出4个爱心/小花
    const particles = ['💖', '💝', '🌸', '✨'];
    for (let i = 0; i < 4; i++) {
      setTimeout(() => {
        const particle = document.createElement('div');
        particle.className = 'love-particle';
        particle.textContent = particles[i];
        particle.style.cssText = `
          left: ${50 + Math.random() * 10}%;
          animation-delay: ${i * 0.1}s;
        `;
        stage.appendChild(particle);

        setTimeout(() => particle.remove(), 2000);
      }, i * 150);
    }
  }, 3200);

  // 收尾 1.0s - 满足眨眼+退场
  setTimeout(() => {
    const monsterBody = monster.querySelector('.monster-body') as HTMLElement;
    if (monsterBody) {
      monsterBody.classList.remove('burping');
    }

    // 眨眼效果
    const eyes = monster.querySelectorAll('.monster-eye') as NodeListOf<HTMLElement>;
    eyes.forEach(eye => {
      eye.style.transition = 'height 0.15s ease';
      eye.style.height = '3px';
      setTimeout(() => {
        eye.style.height = '14px';
      }, 150);
    });

    // 退场
    setTimeout(() => {
      monster.style.animation = 'monsterExit 1s ease-in-out forwards';
    }, 300);
  }, 4400);
}

// 气球动画
function playBalloonAnimation(stage: HTMLElement, notepad: HTMLElement) {
  // 创建气球容器
  const balloonContainer = document.createElement('div');
  balloonContainer.className = 'balloon-container';
  balloonContainer.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);';

  // 气球主体
  const balloon = document.createElement('div');
  balloon.className = 'balloon-body';

  // 气球高光
  const highlight = document.createElement('div');
  highlight.className = 'balloon-highlight';
  balloon.appendChild(highlight);

  // 绳子
  const string = document.createElement('div');
  string.className = 'balloon-string';

  // 绳结
  const knot = document.createElement('div');
  knot.className = 'balloon-knot';
  string.appendChild(knot);

  balloonContainer.appendChild(balloon);
  balloonContainer.appendChild(string);

  // 便签克隆（将变成信封）
  const noteClone = notepad.cloneNode(true) as HTMLElement;
  noteClone.className = 'notepad-clone';
  noteClone.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);';

  // 云朵
  const cloud1 = document.createElement('div');
  cloud1.className = 'cloud cloud-1';
  const cloud2 = document.createElement('div');
  cloud2.className = 'cloud cloud-2';

  stage.appendChild(noteClone);
  stage.appendChild(balloonContainer);
  stage.appendChild(cloud1);
  stage.appendChild(cloud2);

  // 阶段1: 气球入场 0.4s
  setTimeout(() => {
    balloonContainer.style.animation = 'balloonEntry 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, balloonSway 2s ease-in-out 0.5s infinite';
  }, 100);

  // 阶段2: 便签折叠成信封 0.7s (关键优化!)
  setTimeout(() => {
    noteClone.style.animation = 'notepadToEnvelope 0.7s cubic-bezier(0.68, -0.55, 0.27, 1.55) forwards';
  }, 500);

  // 阶段3: 信封飞向气球绳子 0.5s
  setTimeout(() => {
    // 移除便签克隆
    noteClone.remove();

    // 创建信封元素
    const envelope = document.createElement('div');
    envelope.className = 'envelope';
    envelope.style.cssText = 'position: absolute; top: 80%; left: 50%; transform: translate(-50%, -50%); opacity: 0;';

    const flap = document.createElement('div');
    flap.className = 'envelope-flap';
    envelope.appendChild(flap);

    stage.appendChild(envelope);

    // 信封飞向气球绳子底部
    setTimeout(() => {
      envelope.style.animation = 'envelopeFlyToBalloon 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards';
    }, 50);

    // 系上绳子
    setTimeout(() => {
      envelope.remove();
      const envelopeOnString = document.createElement('div');
      envelopeOnString.className = 'envelope';
      envelopeOnString.style.cssText = 'position: absolute; bottom: -45px; left: 50%; transform: translateX(-50%);';

      const flapOnString = document.createElement('div');
      flapOnString.className = 'envelope-flap';
      envelopeOnString.appendChild(flapOnString);

      balloonContainer.appendChild(envelopeOnString);

      // 绳结系紧动画
      knot.style.animation = 'stringTie 0.3s cubic-bezier(0.68, -0.55, 0.27, 1.55) forwards';
    }, 500);
  }, 1200);

  // 阶段4: 起飞 2.2s - 信封+气球缓慢上升，穿过两朵云
  setTimeout(() => {
    balloonContainer.style.animation = 'balloonFlyAway 2.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards';

    // 云朵出现并向下漂移（营造上升感）
    cloud1.style.animation = 'cloudDrift 3s ease-in-out forwards';
    setTimeout(() => {
      cloud2.style.animation = 'cloudDrift 3s ease-in-out 0.6s forwards';
    }, 300);
  }, 1700);

  // 阶段5: 远去消失 0.8s - 变小变淡直至消失
  setTimeout(() => {
    balloonContainer.style.animation = 'balloonFlyAway 2.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards, fadeOut 0.8s ease-out 2.2s forwards';
  }, 1700);

  // 收尾 0.6s: 一束柔光扫过
  setTimeout(() => {
    const light = document.createElement('div');
    light.className = 'light-sweep';
    light.style.cssText = `
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
      animation: lightSweep 0.6s ease-out forwards;
    `;
    stage.appendChild(light);
    setTimeout(() => light.remove(), 600);
  }, 4400);
}

// 封存罐动画
function playJarAnimation(stage: HTMLElement, notepad: HTMLElement) {
  // 创建罐子容器
  const jarContainer = document.createElement('div');
  jarContainer.className = 'jar-container';
  jarContainer.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);';

  // 玻璃罐主体
  const jarBody = document.createElement('div');
  jarBody.className = 'jar-body';

  // 瓶盖
  const lid = document.createElement('div');
  lid.className = 'jar-lid';

  // 盖子把手
  const lidHandle = document.createElement('div');
  lidHandle.className = 'jar-lid-handle';
  lid.appendChild(lidHandle);

  // 标签
  const label = document.createElement('div');
  label.className = 'jar-label';
  label.textContent = '今天先到这';
  label.style.opacity = '0';

  jarContainer.appendChild(jarBody);
  jarContainer.appendChild(lid);
  jarContainer.appendChild(label);

  // 便签克隆（将变成纸卷）
  const noteClone = notepad.cloneNode(true) as HTMLElement;
  noteClone.className = 'notepad-clone';
  noteClone.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);';

  stage.appendChild(noteClone);
  stage.appendChild(jarContainer);

  // 阶段1: 罐子入场 0.4s - 玻璃罐从下方弹入，落地轻微晃
  setTimeout(() => {
    jarContainer.style.animation = 'jarEntry 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards';

    // 落地后轻微晃动
    setTimeout(() => {
      jarContainer.style.animation = 'jarWobble 0.6s ease-in-out forwards';
    }, 400);
  }, 100);

  // 阶段2: 便签卷成卷轴 0.7s (关键优化!)
  setTimeout(() => {
    noteClone.style.animation = 'notepadRoll 0.7s cubic-bezier(0.68, -0.55, 0.27, 1.55) forwards';
  }, 1000);

  // 阶段3: 卷轴飞向罐口并掉入 0.9s
  setTimeout(() => {
    // 移除便签克隆
    noteClone.remove();

    // 创建纸卷元素
    const scroll = document.createElement('div');
    scroll.className = 'jar-paper-scroll';
    scroll.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translateX(-50%); opacity: 0;';

    stage.appendChild(scroll);

    // 卷轴飞向罐口上方
    setTimeout(() => {
      scroll.style.animation = 'scrollFlyToJar 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards';
    }, 50);

    // 掉入罐口
    setTimeout(() => {
      scroll.style.animation = 'scrollDrop 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55) forwards';

      // 移除纸卷元素，因为已经"进入"罐内
      setTimeout(() => {
        scroll.remove();
      }, 500);
    }, 450);
  }, 1700);

  // 阶段4: 盖上 0.7s - 盖子"咔"合上，罐子发出一圈柔光
  setTimeout(() => {
    lid.style.animation = 'lidClose 0.7s cubic-bezier(0.68, -0.55, 0.27, 1.55) forwards';

    // 罐子发光效果
    setTimeout(() => {
      jarBody.style.animation = 'jarGlow 0.8s ease-in-out forwards';
    }, 350);
  }, 2600);

  // 阶段5: 贴标签 0.8s - 标签贴上（从侧面滑入）
  setTimeout(() => {
    label.style.animation = 'labelSlideIn 0.8s cubic-bezier(0.68, -0.55, 0.27, 1.55) forwards';
  }, 3300);

  // 收尾 0.4s: 罐子淡出回归初始
  setTimeout(() => {
    jarContainer.style.animation = 'fadeOut 0.4s ease-out forwards';
  }, 4400);
}

// 冰冻动画
function playFreezeAnimation(stage: HTMLElement, notepad: HTMLElement) {
  // 创建冰冻容器
  const freezeContainer = document.createElement('div');
  freezeContainer.className = 'freeze-container';

  // 冰块主体
  const iceCube = document.createElement('div');
  iceCube.className = 'ice-cube';

  // 冰块内气泡
  for (let i = 0; i < 3; i++) {
    const bubble = document.createElement('div');
    bubble.className = 'ice-bubble';
    iceCube.appendChild(bubble);
  }

  // 霜花纹理
  const frostPattern = document.createElement('div');
  frostPattern.className = 'frost-pattern';
  iceCube.appendChild(frostPattern);

  freezeContainer.appendChild(iceCube);

  // 冷气雾（3层）
  const mists = [];
  for (let i = 1; i <= 3; i++) {
    const mist = document.createElement('div');
    mist.className = `cold-mist layer-${i}`;
    freezeContainer.appendChild(mist);
    mists.push(mist);
  }

  // 雪花粒子（14个）
  const snowflakes = [];
  for (let i = 0; i < 14; i++) {
    const snowflake = document.createElement('div');
    snowflake.className = `snowflake type-${i % 2 === 0 ? '1' : '2'}`;
    if (i % 2 === 0) {
      snowflake.textContent = '❄';
    }
    // 随机位置
    snowflake.style.left = `${Math.random() * 180 + 10}px`;
    snowflake.style.top = `${Math.random() * 80}px`;
    snowflake.style.animationDelay = `${0.5 + Math.random() * 1.5}s`;
    snowflake.style.animationDuration = `${2 + Math.random()}s`;
    freezeContainer.appendChild(snowflake);
    snowflakes.push(snowflake);
  }

  // 便签克隆（将被冻住）
  const noteClone = notepad.cloneNode(true) as HTMLElement;
  noteClone.className = 'notepad-clone';
  noteClone.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 2;';

  // 先添加冰块容器,再添加便签,这样便签在冰块上方
  stage.appendChild(freezeContainer);
  stage.appendChild(noteClone);

  // 入场 0.4s: 冰块从下方弹入，表面高光滑过
  setTimeout(() => {
    iceCube.style.animation = 'iceEntry 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards';
  }, 100);

  // 冻结 1.4s: 便签泛蓝→边缘结霜
  setTimeout(() => {
    // 便签变蓝
    noteClone.style.transition = 'all 1.4s ease-out';
    noteClone.style.filter = 'hue-rotate(180deg) brightness(1.1)';
    noteClone.style.opacity = '0.7';

    // 边缘结霜效果（通过box-shadow模拟）
    setTimeout(() => {
      noteClone.style.boxShadow = '0 0 20px rgba(186, 230, 253, 0.8), inset 0 0 20px rgba(186, 230, 253, 0.3)';
    }, 400);

    // 文字变淡
    const textarea = noteClone.querySelector('textarea');
    if (textarea) {
      (textarea as HTMLElement).style.transition = 'opacity 1.0s ease-out';
      (textarea as HTMLElement).style.opacity = '0.3';
    }
  }, 500);

  // 定格 1.4s: 冰块轻微呼吸，雪花减少
  setTimeout(() => {
    iceCube.style.animation = 'iceBreathing 2s ease-in-out infinite';

    // 停止部分雪花
    snowflakes.forEach((sf, i) => {
      if (i % 3 === 0) {
        sf.style.opacity = '0';
      }
    });
  }, 1900);

  // 解冻小光出现
  setTimeout(() => {
    for (let i = 0; i < 5; i++) {
      const light = document.createElement('div');
      light.className = 'thaw-light';
      light.style.top = `${Math.random() * 160 + 10}px`;
      light.style.left = `${Math.random() * 160 + 10}px`;
      light.style.animationDelay = `${i * 0.1}s`;
      iceCube.appendChild(light);
    }
  }, 3100);

  // 收尾 1.4s: 冰雾散去，冰块下沉退场
  setTimeout(() => {
    // 雾气消失
    mists.forEach(m => {
      m.style.opacity = '0';
    });

    // 冰块下沉
    setTimeout(() => {
      freezeContainer.style.animation = 'iceSinkAway 1.0s cubic-bezier(0.6, 0.04, 0.98, 0.34) forwards';
      noteClone.style.animation = 'fadeOut 1.0s ease-out forwards';
    }, 400);
  }, 3300);
}

// 种花动画
function playPlantAnimation(stage: HTMLElement, notepad: HTMLElement) {
  // 创建种花容器
  const plantContainer = document.createElement('div');
  plantContainer.className = 'plant-container';

  // 花盆
  const pot = document.createElement('div');
  pot.className = 'flower-pot';

  // 种子
  const seed = document.createElement('div');
  seed.className = 'seed';
  seed.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);';

  // 茎
  const stem = document.createElement('div');
  stem.className = 'sprout-stem';

  // 叶子
  const leafLeft = document.createElement('div');
  leafLeft.className = 'leaf left';
  const leafRight = document.createElement('div');
  leafRight.className = 'leaf right';
  stem.appendChild(leafLeft);
  stem.appendChild(leafRight);

  // 花朵
  const flowerBloom = document.createElement('div');
  flowerBloom.className = 'flower-bloom';

  // 花瓣（5片）
  for (let i = 0; i < 5; i++) {
    const petal = document.createElement('div');
    petal.className = 'petal';
    flowerBloom.appendChild(petal);
  }

  // 花芯
  const flowerCenter = document.createElement('div');
  flowerCenter.className = 'flower-center';
  flowerBloom.appendChild(flowerCenter);

  stem.appendChild(flowerBloom);
  plantContainer.appendChild(pot);
  plantContainer.appendChild(stem);

  // 阳光光斑
  const sunlight = document.createElement('div');
  sunlight.className = 'sunlight';
  sunlight.style.cssText = 'position: absolute; top: 20px; left: 50%; transform: translateX(-50%);';

  // 花粉光点
  const pollens = [];
  for (let i = 0; i < 8; i++) {
    const pollen = document.createElement('div');
    pollen.className = 'pollen';
    const angle = (Math.PI * 2 * i) / 8;
    const distance = 40 + Math.random() * 20;
    pollen.style.cssText = `
      position: absolute;
      top: 50px;
      left: 110px;
      --x: ${Math.cos(angle) * distance}px;
      --y: ${Math.sin(angle) * distance}px;
    `;
    pollen.style.animationDelay = `${3.3 + i * 0.1}s`;
    pollen.style.animation = 'pollenFloat 1.2s ease-out forwards';
    plantContainer.appendChild(pollen);
    pollens.push(pollen);
  }

  plantContainer.appendChild(sunlight);

  // 便签克隆（将变成种子）
  const noteClone = notepad.cloneNode(true) as HTMLElement;
  noteClone.className = 'notepad-clone';
  noteClone.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);';

  stage.appendChild(noteClone);
  stage.appendChild(plantContainer);
  stage.appendChild(seed);

  // 入场 0.5s: 花盆弹入，轻抖落定
  setTimeout(() => {
    pot.style.animation = 'potEntry 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards';

    // 落定后轻抖
    setTimeout(() => {
      pot.style.animation = 'potShake 0.3s ease-in-out forwards';
    }, 500);
  }, 100);

  // 埋下 0.8s: 便签缩小成种子掉入土里
  setTimeout(() => {
    noteClone.style.transition = 'all 0.8s cubic-bezier(0.68, -0.55, 0.27, 1.55)';
    noteClone.style.transform = 'translate(-50%, -50%) scale(0.1)';
    noteClone.remove();

    // 种子显现并掉落
    setTimeout(() => {
      seed.style.opacity = '1';
      seed.style.top = '30%';
      seed.style.animation = 'seedDrop 0.6s cubic-bezier(0.6, 0.04, 0.98, 0.34) forwards';
    }, 200);
  }, 600);

  // 发芽 1.4s: 小芽从土里顶出，叶子展开
  setTimeout(() => {
    stem.style.animation = 'sproutGrow 1.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards';

    // 叶子展开
    setTimeout(() => {
      leafLeft.style.animation = 'leafUnfold 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards';
      leafRight.style.animation = 'leafUnfoldRight 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards';
    }, 600);
  }, 1400);

  // 开花 1.6s: 花苞长出→轻轻绽放（两段式）
  setTimeout(() => {
    const petals = flowerBloom.querySelectorAll('.petal');

    // 第一段：40% 绽放
    petals.forEach((petal, i) => {
      (petal as HTMLElement).style.animation = `petalBloom 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 0.08}s forwards`;
    });

    // 第二段：80%→100% 完全绽放
    setTimeout(() => {
      petals.forEach((petal, i) => {
        (petal as HTMLElement).style.transform = `scale(1)`;
        (petal as HTMLElement).style.opacity = '1';
      });
    }, 800);
  }, 2800);

  // 收尾 1.3s: 阳光光斑扫过，花轻微摇摆
  setTimeout(() => {
    sunlight.style.animation = 'sunlightSweep 1.3s ease-out forwards';

    // 花朵摇摆
    stem.style.animation = 'flowerSway 2s ease-in-out infinite';
  }, 4300);
}

// 烧掉动画 - 优化版 4.2s
function playBurnAnimation(stage: HTMLElement, notepad: HTMLElement) {
  // 创建烧掉容器
  const burnContainer = document.createElement('div');
  burnContainer.className = 'burn-container';

  // 小火苗主体（治愈小精灵）
  const flame = document.createElement('div');
  flame.className = 'cute-flame';

  // 火苗内焰
  const flameInner = document.createElement('div');
  flameInner.className = 'flame-inner';
  flame.appendChild(flameInner);

  burnContainer.appendChild(flame);

  // 便签克隆（将被净化成光）
  const noteClone = notepad.cloneNode(true) as HTMLElement;
  noteClone.className = 'notepad-clone burn-note';
  noteClone.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);';

  stage.appendChild(noteClone);
  stage.appendChild(burnContainer);

  // Phase 0: 入场与聚焦 0.0-0.4s
  setTimeout(() => {
    // 火苗从下方弹入
    flame.style.animation = 'flameEntry 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards';

    // 火苗呼吸一次
    setTimeout(() => {
      flame.style.animation = 'flameBreathOnce 0.5s cubic-bezier(0.45, 0.05, 0.55, 0.95) forwards';
    }, 350);
  }, 100);

  // Phase 1: 便签被吸引飞向火焰 0.4-1.0s (关键优化!)
  setTimeout(() => {
    noteClone.style.transition = 'transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)';
    noteClone.style.transform = 'translate(-50%, -52%)';

    // 火苗变大,像在吸引便签
    setTimeout(() => {
      flame.style.transition = 'transform 0.4s ease-out';
      flame.style.transform = 'translateX(-50%) scale(1.15)';
    }, 100);
  }, 400);

  // Phase 2: 便签开始卷曲进入火焰 1.0-1.6s
  setTimeout(() => {
    noteClone.style.animation = 'notepadCurlToFlame 0.6s ease-in-out forwards';
  }, 1000);

  // Phase 3: 燃烧净化成光点 1.6-3.2s (分3批释放)
  setTimeout(() => {
    // 便签文字开始变淡
    const textarea = noteClone.querySelector('textarea');
    if (textarea) {
      (textarea as HTMLElement).style.transition = 'opacity 1.6s ease-out';
      (textarea as HTMLElement).style.opacity = '0';
    }

    // 火苗温柔变大
    flame.style.animation = 'flameGentleGrow 1.6s cubic-bezier(0.45, 0.05, 0.55, 0.95) forwards';

    // 批次A: 1.6-2.0s (7个光点)
    setTimeout(() => {
      createLightSparks(burnContainer, 7, 0, 'batch-a');
    }, 0);

    // 批次B: 2.0-2.5s (7个光点)
    setTimeout(() => {
      createLightSparks(burnContainer, 7, 0, 'batch-b');
    }, 400);

    // 批次C: 2.5-3.2s (5个光点 + 2个小星)
    setTimeout(() => {
      createLightSparks(burnContainer, 5, 2, 'batch-c');
    }, 900);
  }, 1600);

  // Phase 4: 散去与安静 3.2-4.0s
  setTimeout(() => {
    // 便签整体变轻消失
    noteClone.style.transition = 'opacity 0.8s ease-out';
    noteClone.remove();

    // 留下淡金光晕
    const halo = document.createElement('div');
    halo.className = 'warm-halo';
    halo.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    `;
    halo.style.animation = 'haloAppear 0.3s ease-out forwards, haloFade 0.5s ease-out 0.3s forwards';
    burnContainer.appendChild(halo);
  }, 3200);

  // Phase 5: 收尾与回归 4.0-4.2s
  setTimeout(() => {
    // 火苗缩小退场
    flame.style.animation = 'flameShrink 0.2s cubic-bezier(0.6, 0.04, 0.98, 0.34) forwards';
  }, 4000);
}

// 创建光点粒子的辅助函数
function createLightSparks(container: HTMLElement, sparkCount: number, starCount: number, batchClass: string) {
  // 创建圆点光粒
  for (let i = 0; i < sparkCount; i++) {
    const spark = document.createElement('div');
    spark.className = `light-spark ${batchClass}`;

    // 用户规格：上升 40-90px，左右漂移 10-18px
    const xOffset = (Math.random() - 0.5) * 18;  // 左右漂移 -9 到 +9px (范围18px)
    const yOffset = -(40 + Math.random() * 50);   // 上升 40-90px

    spark.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      --x: ${xOffset}px;
      --y: ${yOffset}px;
      --delay: ${i * 0.12}s;
    `;
    spark.style.animation = `sparkRise ${2.2 + Math.random() * 0.6}s ease-out var(--delay) forwards`;

    container.appendChild(spark);
  }

  // 创建小星星点缀
  for (let i = 0; i < starCount; i++) {
    const star = document.createElement('div');
    star.className = `light-star ${batchClass}`;
    star.textContent = '✨';

    // 用户规格：上升 40-90px，左右漂移 10-18px（星星稍微飘得更远一些）
    const xOffset = (Math.random() - 0.5) * 22;  // 左右漂移稍大 -11 到 +11px
    const yOffset = -(45 + Math.random() * 45);   // 上升 45-90px

    star.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      --x: ${xOffset}px;
      --y: ${yOffset}px;
      --delay: ${i * 0.15}s;
    `;
    star.style.animation = `starRise ${2.5 + Math.random() * 0.5}s ease-out var(--delay) forwards`;

    container.appendChild(star);
  }
}

// 折纸动画
function playOrigamiAnimation(stage: HTMLElement, notepad: HTMLElement) {
  // 创建折纸容器
  const origamiContainer = document.createElement('div');
  origamiContainer.className = 'origami-container';

  // 折痕辅助线
  const foldLineDiagonal = document.createElement('div');
  foldLineDiagonal.className = 'fold-line diagonal';
  const foldLineVertical = document.createElement('div');
  foldLineVertical.className = 'fold-line vertical';

  origamiContainer.appendChild(foldLineDiagonal);
  origamiContainer.appendChild(foldLineVertical);

  // 便签克隆（将被折叠）
  const noteClone = notepad.cloneNode(true) as HTMLElement;
  noteClone.className = 'notepad-clone';
  noteClone.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);';

  // 纸鹤
  const paperCrane = document.createElement('div');
  paperCrane.className = 'paper-crane';

  const craneBody = document.createElement('div');
  craneBody.className = 'crane-body';

  const craneHead = document.createElement('div');
  craneHead.className = 'crane-head';

  const craneTail = document.createElement('div');
  craneTail.className = 'crane-tail';

  const wingLeft = document.createElement('div');
  wingLeft.className = 'crane-wing left';

  const wingRight = document.createElement('div');
  wingRight.className = 'crane-wing right';

  craneBody.appendChild(craneHead);
  craneBody.appendChild(craneTail);
  craneBody.appendChild(wingLeft);
  craneBody.appendChild(wingRight);
  paperCrane.appendChild(craneBody);

  // 飞行轨迹线
  const flightTrail = document.createElement('div');
  flightTrail.className = 'flight-trail';
  flightTrail.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);';

  // 轨迹星点
  const trailStars = [];
  for (let i = 0; i < 5; i++) {
    const star = document.createElement('div');
    star.className = 'trail-star';
    origamiContainer.appendChild(star);
    trailStars.push(star);
  }

  stage.appendChild(noteClone);
  stage.appendChild(origamiContainer);
  origamiContainer.appendChild(paperCrane);
  origamiContainer.appendChild(flightTrail);

  // 入场 0.3s: 便签轻上浮，出现折痕辅助线
  setTimeout(() => {
    noteClone.style.transition = 'transform 0.3s ease-out';
    noteClone.style.transform = 'translate(-50%, -55%)';

    foldLineDiagonal.style.animation = 'foldLinesAppear 0.3s ease-out forwards';
    foldLineVertical.style.animation = 'foldLinesAppear 0.3s ease-out 0.15s forwards';
  }, 100);

  // 折纸 1.8s: 纸张按4步折叠
  setTimeout(() => {
    const textarea = noteClone.querySelector('textarea');
    if (textarea) {
      (textarea as HTMLElement).style.transition = 'opacity 1.8s ease-out';
      (textarea as HTMLElement).style.opacity = '0';
    }

    // 第1步：折角
    noteClone.style.animation = 'foldStep1 0.45s cubic-bezier(0.68, -0.55, 0.27, 1.55) forwards';

    // 第2步：对折
    setTimeout(() => {
      noteClone.style.animation = 'foldStep2 0.45s cubic-bezier(0.68, -0.55, 0.27, 1.55) forwards';
    }, 450);

    // 第3步：翻折
    setTimeout(() => {
      noteClone.style.animation = 'foldStep3 0.45s cubic-bezier(0.68, -0.55, 0.27, 1.55) forwards';
    }, 900);

    // 第4步：成型（便签消失）
    setTimeout(() => {
      noteClone.style.animation = 'foldStep4 0.45s cubic-bezier(0.6, 0.04, 0.98, 0.34) forwards';
    }, 1350);
  }, 400);

  // 成鹤 0.8s: 纸鹤"抖一抖翅膀"
  setTimeout(() => {
    paperCrane.style.animation = 'craneAppear 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards';

    // 翅膀拍动两次
    setTimeout(() => {
      wingLeft.style.animation = 'wingFlap 0.4s ease-in-out';
      wingRight.style.animation = 'wingFlap 0.4s ease-in-out';

      // 第二次拍动
      setTimeout(() => {
        wingLeft.style.animation = 'wingFlap 0.4s ease-in-out';
        wingRight.style.animation = 'wingFlap 0.4s ease-in-out';
      }, 400);
    }, 300);
  }, 2200);

  // 起飞 1.6s: 纸鹤沿曲线飞上去
  setTimeout(() => {
    paperCrane.style.animation = 'craneFly 1.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards';

    // 翅膀持续拍动
    wingLeft.style.animation = 'wingFlap 0.5s ease-in-out infinite';
    wingRight.style.animation = 'wingFlap 0.5s ease-in-out infinite';

    // 轨迹线出现
    setTimeout(() => {
      flightTrail.style.animation = 'trailAppear 1.4s ease-out forwards';
    }, 200);

    // 星点闪烁
    trailStars.forEach((star, i) => {
      star.style.cssText = `
        position: absolute;
        top: ${50 - i * 20}%;
        left: ${50 + i * 5}%;
      `;
      star.style.animationDelay = `${i * 0.25}s`;
      star.style.animation = 'starTwinkle 0.8s ease-in-out forwards';
    });
  }, 3000);

  // 收尾 0.9s: 纸鹤变小消失，轨迹淡出
  // （已包含在 craneFly 动画中）
}

// 回收利用动画
function playRecycleAnimation(stage: HTMLElement, notepad: HTMLElement) {
  // 创建回收容器
  const recycleContainer = document.createElement('div');
  recycleContainer.className = 'recycle-container';

  // 回收桶
  const bin = document.createElement('div');
  bin.className = 'recycle-bin';

  // 回收标志
  const symbol = document.createElement('div');
  symbol.className = 'recycle-symbol';
  symbol.textContent = '♻️';
  bin.appendChild(symbol);

  recycleContainer.appendChild(bin);

  // 便签克隆（将变成纸团）
  const noteClone = notepad.cloneNode(true) as HTMLElement;
  noteClone.className = 'notepad-clone';
  noteClone.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);';

  stage.appendChild(noteClone);
  stage.appendChild(recycleContainer);

  // 入场 0.4s: 回收桶弹入，标志轻微转15°回正
  setTimeout(() => {
    bin.style.animation = 'binEntry 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards';
  }, 100);

  // 投递 0.8s: 便签缩小、卷成纸团，沿弧线落入回收桶
  setTimeout(() => {
    // 便签变成纸团
    noteClone.style.transition = 'all 0.4s ease-out';
    noteClone.style.borderRadius = '50%';
    noteClone.style.transform = 'translate(-50%, -50%) scale(0.5)';

    // 创建纸团元素
    setTimeout(() => {
      const paperBall = document.createElement('div');
      paperBall.className = 'paper-ball';
      paperBall.style.cssText = 'position: absolute; top: 50%; left: 50%;';

      recycleContainer.appendChild(paperBall);
      noteClone.remove();

      // 纸团投入桶中
      setTimeout(() => {
        paperBall.style.animation = 'paperThrow 0.6s cubic-bezier(0.6, 0.04, 0.98, 0.34) forwards';
      }, 50);
    }, 400);
  }, 500);

  // 回收循环 1.6s: 回收标志旋转，桶内冒出绿光点
  setTimeout(() => {
    // 标志旋转（慢→快→慢）
    symbol.style.animation = 'symbolRotate 1.6s cubic-bezier(0.45, 0.05, 0.55, 0.95) forwards';

    // 创建绿色光点粒子（10个）
    setTimeout(() => {
      for (let i = 0; i < 10; i++) {
        const particle = document.createElement('div');
        particle.className = 'green-particle';

        // 从回收桶口向上发散
        const angle = (Math.PI * 2 * i) / 10;
        const distance = 30 + Math.random() * 40;
        const xOffset = Math.cos(angle) * distance;
        const yOffset = -60 - Math.random() * 50;

        particle.style.cssText = `
          position: absolute;
          bottom: 150px;
          left: 50%;
          --x: ${xOffset}px;
          --y: ${yOffset}px;
        `;
        particle.style.animationDelay = `${i * 0.08}s`;
        particle.style.animation = `greenParticleRise ${1.2 + Math.random() * 0.5}s ease-out forwards`;

        recycleContainer.appendChild(particle);
      }
    }, 400);
  }, 1300);

  // 生成新物 1.6s: 桶里"噗"地弹出小纸星
  setTimeout(() => {
    const product = document.createElement('div');
    product.className = 'cute-product';

    const star = document.createElement('div');
    star.className = 'paper-star';
    product.appendChild(star);

    recycleContainer.appendChild(product);

    // 产物弹出
    setTimeout(() => {
      product.style.animation = 'productPop 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards';

      // 产物轻弹两下
      setTimeout(() => {
        product.style.animation = 'productPop 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, productBounce 0.6s ease-in-out 0.8s 2';
      }, 0);
    }, 100);

    // 淡金星光（3个）
    setTimeout(() => {
      for (let i = 0; i < 3; i++) {
        const sparkle = document.createElement('div');
        sparkle.className = 'gold-sparkle';
        sparkle.textContent = '✨';
        sparkle.style.cssText = `
          position: absolute;
          top: ${25 + i * 8}%;
          left: ${45 + i * 5}%;
        `;
        sparkle.style.animationDelay = `${i * 0.15}s`;
        sparkle.style.animation = 'goldSparkle 0.8s ease-in-out forwards';
        recycleContainer.appendChild(sparkle);
      }
    }, 600);
  }, 2900);

  // 收尾 0.8s: 产物淡出
  setTimeout(() => {
    const product = recycleContainer.querySelector('.cute-product');
    if (product) {
      (product as HTMLElement).style.animation = 'fadeOut 0.8s ease-out forwards';
    }
  }, 4400);
}

// 锁进盒子动画
function playLockboxAnimation(stage: HTMLElement, notepad: HTMLElement) {
  // 创建锁箱容器
  const lockboxContainer = document.createElement('div');
  lockboxContainer.className = 'lockbox-container';

  // 收纳盒底座
  const box = document.createElement('div');
  box.className = 'storage-box';

  // 云朵内衬
  const cloudLining = document.createElement('div');
  cloudLining.className = 'cloud-lining';
  box.appendChild(cloudLining);

  // 盒盖
  const lid = document.createElement('div');
  lid.className = 'box-lid';

  lockboxContainer.appendChild(box);
  lockboxContainer.appendChild(lid);

  // 锁
  const lock = document.createElement('div');
  lock.className = 'lock';

  const lockBody = document.createElement('div');
  lockBody.className = 'lock-body';

  const keyhole = document.createElement('div');
  keyhole.className = 'lock-keyhole';
  lockBody.appendChild(keyhole);

  const shackle = document.createElement('div');
  shackle.className = 'lock-shackle';

  lock.appendChild(lockBody);
  lock.appendChild(shackle);
  lockboxContainer.appendChild(lock);

  // 安全光晕
  const glow = document.createElement('div');
  glow.className = 'security-glow';
  lockboxContainer.appendChild(glow);

  // 便签克隆（将变成纸卷）
  const noteClone = notepad.cloneNode(true) as HTMLElement;
  noteClone.className = 'notepad-clone';
  noteClone.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);';

  stage.appendChild(noteClone);
  stage.appendChild(lockboxContainer);

  // 入场 0.4s: 盒子弹入，盒盖微微打开
  setTimeout(() => {
    box.style.animation = 'boxEntry 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards';
    lid.style.animation = 'boxEntry 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, lidPeek 1s ease-in-out 0.4s';
  }, 100);

  // 放入 0.9s: 便签卷成纸卷，轻轻落入盒子
  setTimeout(() => {
    // 便签变纸卷
    noteClone.style.transition = 'all 0.5s ease-out';
    noteClone.style.transform = 'translate(-50%, -50%) rotateZ(90deg) scaleX(0.3)';
    noteClone.style.borderRadius = '8px';

    // 创建纸卷元素
    setTimeout(() => {
      const scroll = document.createElement('div');
      scroll.className = 'jar-paper-scroll';
      scroll.style.cssText = 'position: absolute; top: 30%; left: 50%; transform: translateX(-50%);';

      lockboxContainer.appendChild(scroll);
      noteClone.remove();

      // 纸卷落入（带软弹）
      setTimeout(() => {
        scroll.style.animation = 'scrollDrop 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards';
      }, 50);
    }, 400);
  }, 500);

  // 合盖 0.8s: 盖子缓慢合上（前快后慢）
  setTimeout(() => {
    lid.style.animation = 'lidClose 0.8s cubic-bezier(0.6, 0.04, 0.98, 0.34) forwards';
  }, 1400);

  // 上锁 0.9s: 锁出现→锁梁落下→光晕闪现
  setTimeout(() => {
    // 锁出现
    lock.style.animation = 'lockAppear 0.4s ease-out forwards';

    // 锁梁落下
    setTimeout(() => {
      shackle.style.animation = 'shackleLock 0.3s cubic-bezier(0.6, 0.04, 0.98, 0.34) forwards';

      // 光晕闪现
      setTimeout(() => {
        glow.style.animation = 'glowPulse 0.6s ease-out forwards';
      }, 200);
    }, 400);
  }, 2200);

  // 收尾 1.6s: 盒子轻轻呼吸一次后淡出
  setTimeout(() => {
    box.style.animation = 'boxBreath 1.0s ease-in-out forwards';
    lid.style.animation = 'boxBreath 1.0s ease-in-out forwards';

    setTimeout(() => {
      lockboxContainer.style.animation = 'fadeOut 0.6s ease-out forwards';
    }, 1000);
  }, 3100);
}

// 通用动画 - 用于新工具
function playGenericAnimation(stage: HTMLElement, notepad: HTMLElement, tool: Tool) {
  const noteClone = notepad.cloneNode(true) as HTMLElement;
  noteClone.className = 'notepad-clone';

  const icon = document.createElement('div');
  icon.className = 'generic-tool-icon';
  icon.textContent = tool.icon;
  icon.style.fontSize = '4rem';

  stage.appendChild(noteClone);
  stage.appendChild(icon);

  // 便签和图标一起淡出消失
  setTimeout(() => {
    noteClone.style.animation = 'fadeOut 2s ease forwards';
    icon.style.animation = 'scaleUp 2s ease forwards';
  }, 500);
}

// 显示结束文案 (优化: 更温柔的样式和动画)
function showEndMessage(message: string) {
  const endMsg = document.createElement('div');
  endMsg.className = 'end-message';
  endMsg.textContent = message;
  document.body.appendChild(endMsg);

  setTimeout(() => {
    endMsg.classList.add('show');
  }, 10);

  setTimeout(() => {
    endMsg.classList.remove('show');
    setTimeout(() => endMsg.remove(), 500);
  }, 2000);
}

// 创建主界面
function createApp(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'app-container';

  // 标题区
  const header = document.createElement('div');
  header.className = 'header';

  const title = document.createElement('h1');
  title.className = 'title';
  title.textContent = 'Bad Mood Crusher';

  const subtitle = document.createElement('p');
  subtitle.className = 'subtitle';
  subtitle.textContent = subtitles[0];

  header.appendChild(title);
  header.appendChild(subtitle);

  // Carousel subtitle
  setInterval(() => {
    currentSubtitleIndex = (currentSubtitleIndex + 1) % subtitles.length;
    subtitle.style.opacity = '0';
    setTimeout(() => {
      subtitle.textContent = subtitles[currentSubtitleIndex];
      subtitle.style.opacity = '1';
    }, 300);
  }, 5000);

  // 角色
  const character = createCharacter();

  // 便签
  const notepad = createNotepad();

  // 工具栏
  const toolbar = createToolbar();

  container.appendChild(character);
  container.appendChild(header);
  container.appendChild(notepad);
  container.appendChild(toolbar);

  return container;
}

// 初始化应用
function render() {
  const app = document.getElementById('app');
  if (app) {
    app.innerHTML = '';
    app.appendChild(createApp());
  }
}

render();

// Vite HMR
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    render();
  });
}
